import times from 'lodash/times';
import minBy from 'lodash/minBy';
import {twMerge} from 'tailwind-merge';
import {useWindowSize} from 'usehooks-ts';
import getImageAltText from 'src/utils/getImageAltText';
import getImageUrl from 'src/utils/getImageUrl';
import FadingImage from '../FadingImage';
import BaseButton from '../BaseButton';

const getNumberOfColumns = (containerWidth: number) => {
  if (containerWidth > 1200) return 3;

  return 2;
};

const getImagesInColumns = (images: PlaceImage[], columnCount: number) => {
  const columns = times(columnCount).map(() => ({images: [] as PlaceImage[], ratios: 0}));

  for (const image of images) {
    const nextColumn = minBy(columns, (column) => column.ratios);

    if (!nextColumn) continue;

    nextColumn.ratios += image.height / image.width;
    nextColumn.images.push(image);
  }

  return columns;
};

type Props = {
  placeId: string;
  placeName: string;
  countryName: string;
  images: PlaceImage[];
  className?: string;
  setActiveImageId: (imageId: string, placeId?: string) => void;
};

const ImageWall = ({placeId, placeName, countryName, images, className, setActiveImageId}: Props) => {
  const {width} = useWindowSize();
  const columnCount = getNumberOfColumns(width);
  const imagesColumns = getImagesInColumns(images, columnCount);

  return (
    <div className={twMerge('flex flex-wrap relative gap-8 max-[800px]:gap-4', className)}>
      {imagesColumns.map(({images: columnImages}, columnIndex) => {
        return (
          <div key={columnIndex} className="w-0 grow flex flex-col gap-8 max-[800px]:gap-4">
            {columnImages.map((image, imageIndex) => {
              const isNext = imageIndex === 0 && columnIndex === 0;

              return (
                <div key={image._id} className="relative">
                  <BaseButton
                    onPress={() => setActiveImageId(image._id, placeId)}
                    className="relative h-auto p-0 rounded-2xl focus-visible:ring-offset-1 focus-visible:ring-2 focus:ring-gray-200 bg-(--subtle-color) hover:brightness-90 transition duration-200 data-[pressed]:scale-[0.97]"
                  >
                    <FadingImage
                      className="rounded-2xl"
                      src={getImageUrl(image.path)}
                      width={image.width}
                      height={image.height}
                      alt={getImageAltText(image, placeName, countryName)}
                      // This is to "preload" next image
                      sizes={isNext ? '100vw' : '(max-width: 1200px) 50vw, 33vw'}
                    />
                  </BaseButton>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ImageWall;
